import { HTMLAttributes, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { useIsomorphicLayoutEffect } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Logo from '@/components/base/Logo'
import Menu from '@/components/layout/Menu'
import { useStore } from '@/contexts/store'
import { useTimeline } from '@/contexts/timeline'
import { useLayoutStore } from '@/store/layout'
import { useLoader } from '@/store/loader'
import { useRouterStore } from '@/store/router'
import { isLgScreen } from '@/utils/screen'

import CloseButton from '../base/CloseButton'
import MenuButton from '../base/MenuButton'
import { cn } from '@/lib/utils'

export type HeaderProps = HTMLAttributes<HTMLElement>

const Header = ({ className, ...props }: HeaderProps) => {
  const { strings } = useStore()
  const root = useRef<HTMLElement>(null!)
  const menu = useRef<HTMLDivElement>(null!)
  const menuButton = useRef<HTMLButtonElement>(null!)
  const closeButton = useRef<HTMLButtonElement>(null!)
  const { context, contextSafe } = useGSAP({ scope: root })
  const menuOpen = useLayoutStore((state) => state.menuOpen)
  const setLayoutState = useLayoutStore((state) => state.setState)
  const timeline = useTimeline()
  const router = useRouter()

  const openMenu = () => {
    setLayoutState({ menuOpen: true, scrollLocked: true })
  }

  const closeMenu = () => {
    setLayoutState({ menuOpen: false, scrollLocked: false })
  }

  const reveal = contextSafe(() => {
    console.log('header.reveal')

    const logo = context.selector?.('[data-motion-logo]')
    const panel = context.selector?.('[data-motion-panel]')
    const hambLine1 = context.selector?.('[data-motion-hamb-line-top]')
    const hambLine2 = context.selector?.('[data-motion-hamb-line-bot]')

    const timeline = gsap.timeline()

    if (!isLgScreen()) {
      timeline.fromTo(
        panel,
        { yPercent: -100 },
        { yPercent: 0, duration: 1.2, ease: 'power3.inOut' },
        0
      )
    }

    timeline
      .fromTo(
        logo,
        { yPercent: 110, autoAlpha: 0 },
        { yPercent: 0, autoAlpha: 1, duration: 1, ease: 'power3.inOut' },
        !isLgScreen() ? 0.2 : 0
      )
      .fromTo(
        [hambLine1, hambLine2],
        { scaleX: 0, x: 15 },
        { scaleX: 1, x: 0, duration: 1, ease: 'power3.inOut', stagger: 0.1 },
        !isLgScreen() ? 0.3 : 0.2
      )

    return timeline
  })

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = useLoader.subscribe(
      (state) => state.compiled,
      (compiled) => {
        if (compiled) {
          unsubscribe()
          if (router.asPath === '/') {
            timeline.add(reveal(), 2.8)
          }
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = useRouterStore.subscribe((state) => state.currentRoute, closeMenu)

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  return (
    <header ref={root} className={cn(['static', className])} {...props}>
      <span
        className="fixed left-0 top-0 z-header h-[var(--header-height)] w-full bg-black-dark md:hidden"
        data-motion-panel
      />

      <div className="fixed left-[4rem] top-spacing-sm z-logo overflow-hidden md:left-margin md:top-spacing-lg">
        <Link
          href="/"
          scroll={false}
          className={cn(
            'relative -m-spacing-xxs flex p-spacing-xxs',
            router.pathname === '/' ? 'pointer-events-none' : 'pointer-events-all'
          )}
        >
          <span className="sr-only">{strings?.brand}</span>
          <Logo className="h-[1.3rem] w-[5.3rem] md:h-[1.7rem] md:w-[6.9rem]" data-motion-logo />
        </Link>
      </div>

      <Transition
        nodeRef={menuButton}
        in={!menuOpen}
        timeout={{
          enter: 0,
          exit: 575
        }}
        unmountOnExit
      >
        {(state) => <MenuButton ref={menuButton} state={state} onClick={openMenu} />}
      </Transition>

      <Transition
        nodeRef={closeButton}
        in={menuOpen}
        timeout={{
          enter: 0,
          exit: 375
        }}
        unmountOnExit
      >
        {(state) => <CloseButton ref={closeButton} state={state} onClick={closeMenu} />}
      </Transition>

      <Transition
        nodeRef={menu}
        in={menuOpen}
        timeout={{
          enter: 0,
          exit: 500
        }}
        unmountOnExit
      >
        {(state) => <Menu ref={menu} state={state} onClose={closeMenu} />}
      </Transition>
    </header>
  )
}

export default Header
